import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { withAnonHeader } from '@/lib/anonIdentity';

type Msg = { role: 'user' | 'assistant'; content: string };

interface Props {
  requestId: string;
  crImage?: File | null;
  drImage?: File | null;
  onNewQuestion?: () => void;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function readError(error: any): Promise<{ message: string; code?: string }> {
  try {
    const body = await error?.context?.json?.();
    if (body?.error) return { message: body.error, code: body.code };
  } catch { /* ignore */ }
  return { message: "Your message didn't send. It's still here — please try again." };
}

export function CaseConversation({ requestId, crImage, drImage, onNewQuestion }: Props) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{ message: string; code?: string } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const photosSent = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setMessages([]);
    setNotice(null);
    photosSent.current = false;
    (async () => {
      const headers = await withAnonHeader({});
      const { data } = await supabase.functions.invoke('hpe-followup', {
        body: { action: 'load', request_id: requestId },
        headers,
      });
      if (!cancelled && Array.isArray((data as any)?.messages)) {
        setMessages((data as any).messages.map((m: any) => ({ role: m.role, content: m.content })));
      }
    })();
    return () => { cancelled = true; };
  }, [requestId]);

  useEffect(() => {
    if (messages.length || sending) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages.length, sending]);

  async function send() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setNotice(null);
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setDraft('');
    try {
      const body: Record<string, unknown> = { action: 'send', request_id: requestId, message: text };
      if (!photosSent.current) {
        if (crImage) { body.crImageBase64 = await fileToBase64(crImage); body.crMimeType = crImage.type; }
        if (drImage) { body.drImageBase64 = await fileToBase64(drImage); body.drMimeType = drImage.type; }
      }
      const headers = await withAnonHeader({});
      const { data, error } = await supabase.functions.invoke('hpe-followup', { body, headers });
      if (error || !(data as any)?.reply) {
        const err = error ? await readError(error) : { message: "HPE didn't answer. Please try again." };
        setMessages((m) => m.slice(0, -1));
        setDraft(text); // preserve their message
        setNotice(err);
        return;
      }
      photosSent.current = true;
      setMessages((m) => [...m, { role: 'assistant', content: (data as any).reply }]);
    } catch {
      setMessages((m) => m.slice(0, -1));
      setDraft(text);
      setNotice({ message: "Your message didn't send. It's still here — please try again." });
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="rounded-2xl border border-primary/40 bg-card p-5 shadow-sm sm:p-6" aria-label="Follow-up conversation with HPE">
      <h3 className="font-display text-xl font-semibold text-foreground">Let’s think this through together.</h3>
      <p className="mt-1.5 font-body text-[15px] leading-relaxed text-muted-foreground">
        Any questions about your next step? Tell me what you’re seeing, what you have available, or what you’re unsure about.
      </p>

      {messages.length > 0 && (
        <div className="mt-5 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div
                className={
                  m.role === 'user'
                    ? 'max-w-[85%] rounded-2xl rounded-br-md bg-primary px-4 py-3 font-body text-[15px] leading-relaxed text-primary-foreground whitespace-pre-wrap break-words'
                    : 'max-w-[90%] rounded-2xl rounded-bl-md border border-border bg-background px-4 py-3 font-body text-[15px] leading-relaxed text-foreground whitespace-pre-wrap break-words'
                }
              >
                {m.content}
              </div>
            </div>
          ))}
        </div>
      )}

      {sending && (
        <div className="mt-3 flex justify-start" aria-live="polite">
          <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-border bg-background px-4 py-3">
            <span className="sr-only">HPE is typing</span>
            {[0, 1, 2].map((d) => (
              <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-primary" style={{ animationDelay: `${d * 150}ms` }} />
            ))}
          </div>
        </div>
      )}
      <div ref={endRef} />

      {notice && (
        <div role="alert" className="mt-4 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
          <p className="font-body text-sm text-foreground">{notice.message}</p>
          {(notice.code === 'new_case_required' || notice.code === 'case_not_found') && onNewQuestion && (
            <button onClick={onNewQuestion} className="mt-2 font-body text-sm font-semibold text-primary underline-offset-2 hover:underline">
              Start a new question
            </button>
          )}
          {notice.code === 'paywall_required' && (
            <button onClick={() => navigate('/pricing')} className="mt-2 inline-flex items-center gap-1.5 font-body text-sm font-semibold text-primary underline-offset-2 hover:underline">
              <Sparkles className="h-4 w-4" /> Unlock Founding Stylist Access
            </button>
          )}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="mt-4 flex items-end gap-2"
      >
        <label htmlFor="hpe-followup" className="sr-only">Message HPE</label>
        <textarea
          id="hpe-followup"
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Talk it through with HPE…"
          maxLength={4000}
          className="min-h-[52px] flex-1 resize-none rounded-xl border border-border bg-background px-4 py-3 font-body text-base text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="inline-flex h-[52px] items-center gap-1.5 rounded-xl bg-primary px-5 font-body text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          <Send className="h-4 w-4" /> Send
        </button>
      </form>
    </section>
  );
}
